const hasDigit = (value) => /\d/.test(value);

const isParenBalanced = (value) => {
  let depth = 0;
  for (const ch of value) {
    if (ch === '(') {
      depth += 1;
      continue;
    }
    if (ch === ')') {
      depth -= 1;
      if (depth < 0) return false;
    }
  }
  return depth === 0;
};

const matchTrailingWeight = (inner) => {
  const match = inner.match(/^(.*):(\d*\.?\d+)$/);
  if (!match || !hasDigit(match[2]) || !isParenBalanced(match[1])) return null;
  return inner.slice(match[1].length);
};

const peelOuterParens = (body) => {
  let start = 0;
  let end = body.length;
  let prefix = '';
  let suffix = '';

  while (start < end && body[start] === '(' && body[end - 1] === ')') {
    const inner = body.slice(start + 1, end - 1);
    if (!isParenBalanced(inner)) break;

    const weightMarker = matchTrailingWeight(inner);
    if (weightMarker) {
      prefix += '(';
      suffix = `${weightMarker})${suffix}`;
      start += 1;
      end -= 1 + weightMarker.length;
      continue;
    }

    prefix += '(';
    suffix = `)${suffix}`;
    start += 1;
    end -= 1;
  }

  if (!prefix) return null;
  return { prefix, inner: body.slice(start, end), suffix };
};

const pushWhitespaceSplit = (parts, piece, kind) => {
  const match = piece.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const leading = match ? match[1] : '';
  const body = match ? match[2] : piece;
  const trailing = match ? match[3] : '';
  if (leading) parts.push({ text: leading, kind: 'plain' });
  if (body) parts.push({ text: body, kind });
  if (trailing) parts.push({ text: trailing, kind: 'plain' });
};

const appendInnerTags = (parts, inner) => {
  let depth = 0;
  let start = 0;

  for (let index = 0; index <= inner.length; index += 1) {
    const ch = inner[index];
    const isEnd = index === inner.length;
    if (!isEnd && ch === '(') {
      depth += 1;
      continue;
    }
    if (!isEnd && ch === ')') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (!isEnd && !(depth === 0 && (ch === ',' || ch === '\n'))) continue;

    pushWhitespaceSplit(parts, inner.slice(start, index), 'tag');
    if (!isEnd) parts.push({ text: ch, kind: 'plain' });
    start = index + 1;
  }
};

const appendSegmentParts = (parts, segment) => {
  const match = segment.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const leading = match ? match[1] : '';
  const body = match ? match[2] : segment;
  const trailing = match ? match[3] : '';
  if (leading) parts.push({ text: leading, kind: 'plain' });
  if (!body) {
    if (trailing) parts.push({ text: trailing, kind: 'plain' });
    return;
  }

  if (/[\[\]]/.test(body)) {
    parts.push({ text: body, kind: 'unrecognized' });
    if (trailing) parts.push({ text: trailing, kind: 'plain' });
    return;
  }

  const peeled = peelOuterParens(body);
  if (!peeled) {
    parts.push({ text: body, kind: 'tag' });
    if (trailing) parts.push({ text: trailing, kind: 'plain' });
    return;
  }

  if (peeled.prefix) parts.push({ text: peeled.prefix, kind: 'plain' });
  appendInnerTags(parts, peeled.inner);
  if (peeled.suffix) parts.push({ text: peeled.suffix, kind: 'plain' });
  if (trailing) parts.push({ text: trailing, kind: 'plain' });
};

export const tokenizePromptForHighlight = (text) => {
  const source = String(text ?? '');
  const parts = [];
  let segmentStart = 0;
  let parenDepth = 0;
  let bracketDepth = 0;

  for (let index = 0; index < source.length; index += 1) {
    const ch = source[index];
    if (ch === '(') parenDepth += 1;
    else if (ch === ')') parenDepth = Math.max(0, parenDepth - 1);
    else if (ch === '[') bracketDepth += 1;
    else if (ch === ']') bracketDepth = Math.max(0, bracketDepth - 1);
    else if (parenDepth === 0 && bracketDepth === 0 && (ch === ',' || ch === '\n')) {
      appendSegmentParts(parts, source.slice(segmentStart, index));
      parts.push({ text: ch, kind: 'plain' });
      segmentStart = index + 1;
    }
  }

  appendSegmentParts(parts, source.slice(segmentStart));
  return parts.filter((part) => part.text);
};

export const collectLookupTags = (text) => {
  const tags = [];
  let hasSquareBrackets = false;
  tokenizePromptForHighlight(text).forEach((part) => {
    if (part.kind === 'unrecognized') {
      hasSquareBrackets = true;
      return;
    }
    if (part.kind === 'tag') tags.push(part.text);
  });
  return { tags, hasSquareBrackets };
};

const stripActiveTagSyntax = (word) => {
  let current = String(word || '').trim();
  let depth = 0;
  for (const ch of current) {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
  }
  while (depth < 0 && current.endsWith(')')) {
    current = current.slice(0, -1);
    depth += 1;
  }
  return current.replace(/:\d*\.?\d+$/, '').trim();
};

export const findActiveTagStart = (textBeforePosition, splitOnSpaces = false) => {
  const source = String(textBeforePosition || '');
  let start = 0;
  let parenDepth = 0;
  let bracketDepth = 0;

  for (let index = 0; index < source.length; index += 1) {
    const ch = source[index];
    if (ch === '(') {
      if (parenDepth === 0 && bracketDepth === 0 && source.slice(start, index).trim() === '') {
        start = index + 1;
      }
      parenDepth += 1;
      continue;
    }
    if (ch === ')') {
      parenDepth = Math.max(0, parenDepth - 1);
      continue;
    }
    if (ch === '[') {
      bracketDepth += 1;
      continue;
    }
    if (ch === ']') {
      bracketDepth = Math.max(0, bracketDepth - 1);
      continue;
    }
    if (bracketDepth > 0 || parenDepth > 0) continue;
    if (ch === ',' || ch === '\n' || (splitOnSpaces && ch === ' ')) start = index + 1;
  }

  return start;
};

export const readActiveTagWord = (textBeforePosition, splitOnSpaces = false) => {
  const start = findActiveTagStart(textBeforePosition, splitOnSpaces);
  return stripActiveTagSyntax(String(textBeforePosition || '').slice(start));
};
